import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Upload, X, Save, Loader2, ArrowLeft, Sparkles, Plus, MapPin, Wand2, Trash2, Image as ImageIcon, ChevronLeft, ChevronRight, Star, Globe, Users, EyeOff } from "lucide-react";
import { AdminShell } from "./AdminShell";
import { adminApi, mediaUrl } from "./adminApi";
import { fetchListing } from "../api";

const TYPES = ["Appartement", "Maison", "Terrain", "Immeuble"];
const COLLECTIONS = ["Habitat", "Signature", "Prestige"];
const STATUSES = ["Disponible", "Sous compromis", "Vendu"];

const empty = {
  title: "", summary: "", description: "", price: "", city: "", department: "Oise (60)",
  region: "Hauts-de-France", type: "Appartement", surface: "", land: "", bedrooms: "", rooms: "",
  status: "Disponible", collection: "Habitat", dpe: "", lat: "", lng: "", featured: false,
  access_password: "",
  commission_rate: "",
  visibility_mode: "national", network_also_national: false, confidential_mode: "classic",
  cover: "", images: [], highlights: [], published: true,
  ai_content: null, staging: [], seo: null, features: [],
};

const AI_CATEGORIES = ["Nature", "Patrimoine", "Randonnée", "Sport", "Culture", "Famille", "Loisirs", "Gastronomie"];
const emptyAi = { intro: "", history: "", commerces: "", advantages: [], activities: [] };

const STAGING_ROOMS = ["Salon", "Séjour", "Cuisine", "Chambre", "Salle de bain", "Bureau", "Salle à manger", "Terrasse", "Extérieur"];
const STAGING_STYLES = [
  { key: "scandinave", label: "Scandinave" },
  { key: "contemporain", label: "Contemporain" },
  { key: "industriel", label: "Industriel" },
  { key: "luxe", label: "Luxe" },
];
const LUXE_MIN_PRICE = 299999;

export default function ListingForm() {
  const { slug } = useParams();
  const editing = Boolean(slug);
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [importWarning, setImportWarning] = useState("");
  const [uploading, setUploading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const coverRef = useRef();
  const galleryRef = useRef();
  const videoRef = useRef();

  const ai = f.ai_content || emptyAi;
  const setAi = (patch) => setF((p) => ({ ...p, ai_content: { ...(p.ai_content || emptyAi), ...patch } }));

  const pollGeneration = async () => {
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      const d = await fetchListing(slug);
      if (d && d.generation_status && d.generation_status !== "processing") {
        if (d.generation_status === "error") throw new Error(d.generation_error || "Échec de la génération");
        return d;
      }
    }
    throw new Error("Génération trop longue — réessayez dans un instant.");
  };

  const generateContent = async () => {
    setErr(""); setGenLoading(true);
    try {
      await adminApi.generateContent(slug);
      const updated = await pollGeneration();
      setF((p) => ({ ...p, ...updated, highlights: updated.highlights || [], images: updated.images || [], ai_content: updated.ai_content || emptyAi }));
    } catch (ex) { setErr(ex.message || "Échec de la génération IA"); }
    finally { setGenLoading(false); }
  };

  const updateActivity = (i, k, v) => {
    const acts = [...(ai.activities || [])];
    acts[i] = { ...acts[i], [k]: v };
    setAi({ activities: acts });
  };
  const removeActivity = (i) => setAi({ activities: (ai.activities || []).filter((_, idx) => idx !== i) });
  const addActivity = () => setAi({ activities: [...(ai.activities || []), { name: "", category: "Culture", distance: "", drive: "", desc: "", image: "" }] });

  // Home Staging IA
  const staging = f.staging || [];
  const priceNum = parseInt(f.price, 10) || 0;
  const availableStyles = STAGING_STYLES.filter((s) => s.key !== "luxe" || priceNum >= LUXE_MIN_PRICE);
  const [stagingBusy, setStagingBusy] = useState(false);
  const [stagingSel, setStagingSel] = useState({ image_url: "", room: "Salon", style: "contemporain" });

  const generateStaging = async () => {
    setErr("");
    const image_url = stagingSel.image_url || f.cover || (f.images || [])[0];
    if (!image_url) { setErr("Ajoutez d'abord une photo puis sélectionnez-la pour le staging."); return; }
    const style = availableStyles.some((s) => s.key === stagingSel.style) ? stagingSel.style : availableStyles[0].key;
    setStagingBusy(true);
    try {
      const res = await adminApi.generateStaging(slug, { image_url, room: stagingSel.room, style });
      setF((p) => ({ ...p, staging: [...(p.staging || []), res.item] }));
    } catch (ex) { setErr(ex.message || "Échec de la génération du staging"); }
    finally { setStagingBusy(false); }
  };

  const deleteStaging = async (id) => {
    try {
      await adminApi.deleteStaging(slug, id);
      setF((p) => ({ ...p, staging: (p.staging || []).filter((s) => s.id !== id) }));
    } catch (ex) { setErr(ex.message || "Suppression impossible"); }
  };

  const [summaryBusy, setSummaryBusy] = useState(false);
  const genSummary = async () => {
    setErr("");
    if (!(f.description || "").trim()) { setErr("Renseignez d'abord la description."); return; }
    setSummaryBusy(true);
    try {
      const res = await adminApi.generateSummary({ description: f.description, title: f.title });
      setF((p) => ({ ...p, summary: res.summary || "" }));
    } catch (ex) { setErr(ex.message || "Échec de la génération du résumé"); }
    finally { setSummaryBusy(false); }
  };

  const [highlightsBusy, setHighlightsBusy] = useState(false);
  const genHighlights = async () => {
    setErr("");
    if (!(f.description || "").trim() && !(f.title || "").trim()) { setErr("Renseignez d'abord le titre ou la description."); return; }
    setHighlightsBusy(true);
    try {
      const res = await adminApi.generateHighlights({
        title: f.title, description: f.description, type: f.type,
        surface: String(f.surface || ""), land: String(f.land || ""),
        rooms: String(f.rooms || ""), bedrooms: String(f.bedrooms || ""), city: f.city,
      });
      if (Array.isArray(res.highlights) && res.highlights.length) {
        setF((p) => ({ ...p, highlights: res.highlights }));
      }
    } catch (ex) { setErr(ex.message || "Échec de la génération des points forts"); }
    finally { setHighlightsBusy(false); }
  };

  const [actImgBusy, setActImgBusy] = useState(null);
  const regenActivityImage = async (i) => {
    const a = (ai.activities || [])[i] || {};
    setErr(""); setActImgBusy(i);
    try {
      const res = await adminApi.activityImage({ name: a.name || "", category: a.category || "", city: f.city || "" });
      updateActivity(i, "image", res.image);
    } catch (ex) { setErr(ex.message || "Aucune autre image trouvée"); }
    finally { setActImgBusy(null); }
  };

  const [fullBusy, setFullBusy] = useState(false);
  const [galBusy, setGalBusy] = useState(false);
  const [galPreview, setGalPreview] = useState(null);
  const previewGallery = async () => {
    if (!(f.city || "").trim()) { setErr("Renseignez la ville avant de chercher des photos."); return; }
    setErr(""); setGalBusy(true);
    try {
      const r = await adminApi.cityGalleryPreview({ city: f.city || "", department: f.department || "", count: 8 });
      setGalPreview(r.candidates || []);
    } catch (ex) { setErr(ex.message || "Échec de la récupération des photos."); }
    finally { setGalBusy(false); }
  };
  const addGalleryImg = (c) => setAi({ city_gallery: [...(ai.city_gallery || []), { image: c.image, caption: c.caption || (f.city || "") }] });
  const removeGalleryImg = (idx) => setAi({ city_gallery: (ai.city_gallery || []).filter((_, i) => i !== idx) });
  const generateFull = async () => {
    setErr("");
    if (!window.confirm("Générer la fiche complète par IA ? Cela va rédiger le titre, la description, le résumé, les caractéristiques, le contenu local, la galerie ville, les photos d'activités et le SEO. La fiche restera HORS LIGNE (brouillon).")) return;
    setFullBusy(true);
    try {
      await adminApi.generateFull(slug);
      const updated = await pollGeneration();
      setF((p) => ({ ...p, ...updated, highlights: updated.highlights || [], images: updated.images || [], ai_content: updated.ai_content || emptyAi }));
    } catch (ex) { setErr(ex.message || "Échec de la génération complète"); }
    finally { setFullBusy(false); }
  };
  const seo = f.seo || { meta_title: "", meta_description: "", keywords: [], faq: [] };
  const setSeo = (patch) => setF((p) => ({ ...p, seo: { ...(p.seo || { meta_title: "", meta_description: "", keywords: [], faq: [] }), ...patch } }));

  useEffect(() => {
    if (editing) {
      fetchListing(slug).then((d) => {
        if (d) setF({ ...empty, ...d, highlights: d.highlights || [], images: d.images || [] });
        setLoading(false);
      });
    } else if (loc.state?.prefill) {
      const p = loc.state.prefill;
      setF({ ...empty, ...p, published: false, highlights: p.highlights || [], images: p.images || [] });
      if (p.warning) setImportWarning(p.warning);
    }
  }, [slug, editing]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const r = await adminApi.upload(file); set("cover", mediaUrl(r.url)); } catch (ex) { setErr(ex.message); }
    setUploading(false);
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) { const r = await adminApi.upload(file); urls.push(mediaUrl(r.url)); }
      set("images", [...(f.images || []), ...urls]);
    } catch (ex) { setErr(ex.message); }
    setUploading(false);
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const r = await adminApi.upload(file); set("video_url", mediaUrl(r.url)); } catch (ex) { setErr(ex.message); }
    setUploading(false);
  };

  const removeImage = (i) => set("images", f.images.filter((_, idx) => idx !== i));
  const moveImage = (i, dir) => {
    const a = [...(f.images || [])];
    const j = i + dir;
    if (j < 0 || j >= a.length) return;
    [a[i], a[j]] = [a[j], a[i]];
    set("images", a);
  };
  const setAsCover = (src) => set("cover", src);

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    const payload = {
      ...f,
      price: Number(f.price) || 0,
      surface: Number(f.surface) || 0,
      land: Number(f.land) || 0,
      bedrooms: Number(f.bedrooms) || 0,
      rooms: Number(f.rooms) || 0,
      lat: f.lat === "" ? null : Number(f.lat),
      lng: f.lng === "" ? null : Number(f.lng),
      highlights: Array.isArray(f.highlights) ? f.highlights : String(f.highlights).split(",").map((s) => s.trim()).filter(Boolean),
      visibility_mode: f.visibility_mode || "national",
      network_also_national: (f.visibility_mode || "national") === "network" ? !!f.network_also_national : false,
      confidential_mode: (f.access_password || "").trim() ? "password" : "classic",
      commission_rate: (f.commission_rate !== "" && f.commission_rate != null) ? Number(f.commission_rate) : null,
    };
    try {
      if (editing) await adminApi.update(slug, payload);
      else await adminApi.create(payload);
      nav("/admin");
    } catch (ex) { setErr(ex.message || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  const inp = "w-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#C19A6B] transition-colors";
  const lbl = "block text-xs uppercase tracking-wider text-zinc-400 mb-1.5";

  if (loading) return <AdminShell title="Chargement…"><p className="text-zinc-500 text-sm">Chargement…</p></AdminShell>;

  return (
    <AdminShell
      title={editing ? "Modifier l'annonce" : "Nouvelle annonce"}
      action={<button onClick={() => nav("/admin")} className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-white border border-white/10 px-4 py-2.5"><ArrowLeft className="w-4 h-4" /> Retour</button>}
    >
      {err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-3 mb-6" data-testid="form-error">{err}</div>}
      {importWarning && <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm px-4 py-3 mb-6" data-testid="import-warning">⚠️ {importWarning}</div>}

      {editing && (
        <div className="mb-6 border border-[#C19A6B]/30 bg-gradient-to-r from-[#C19A6B]/10 to-transparent p-5 flex flex-wrap items-center justify-between gap-4" data-testid="generate-full-block">
          <div>
            <h3 className="text-white text-sm font-medium uppercase tracking-wider flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C19A6B]" /> Générer la fiche complète</h3>
            <p className="text-xs text-zinc-400 mt-1">Titre, description, résumé, caractéristiques, contenu local, galerie ville, photos d'activités & SEO — en une opération. Reste hors ligne.</p>
          </div>
          <button type="button" onClick={generateFull} disabled={fullBusy} className="flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm font-semibold uppercase tracking-wider px-6 py-3.5 transition-colors disabled:opacity-60" data-testid="generate-full-btn">
            {fullBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération (~1-2 min)…</> : <><Sparkles className="w-4 h-4" /> Générer la fiche complète</>}
          </button>
        </div>
      )}

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="listing-form">
        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <label className={lbl}>Titre *</label>
            <input required value={f.title} onChange={(e) => set("title", e.target.value)} className={inp} data-testid="form-title" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={`${lbl} mb-0`}>Résumé</label>
              <button type="button" onClick={genSummary} disabled={summaryBusy} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#C19A6B] hover:text-[#d1af84] disabled:opacity-50" data-testid="summary-generate-btn">
                {summaryBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Générer le résumé
              </button>
            </div>
            <input value={f.summary} onChange={(e) => set("summary", e.target.value)} className={inp} data-testid="form-summary" />
          </div>
          <div>
            <label className={lbl}>Description</label>
            <textarea rows={5} value={f.description} onChange={(e) => set("description", e.target.value)} className={inp} data-testid="form-description" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><label className={lbl}>Surface (m²)</label><input type="number" value={f.surface} onChange={(e) => set("surface", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Terrain (m²)</label><input type="number" value={f.land} onChange={(e) => set("land", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Pièces</label><input type="number" value={f.rooms} onChange={(e) => set("rooms", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Chambres</label><input type="number" value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className={inp} /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={lbl}>Points forts (séparés par des virgules)</label>
              <button type="button" onClick={genHighlights} disabled={highlightsBusy} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#C19A6B] hover:text-[#d1af84] disabled:opacity-50" data-testid="highlights-generate-btn">
                {highlightsBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Générer les points forts (IA)
              </button>
            </div>
            <input value={Array.isArray(f.highlights) ? f.highlights.join(", ") : f.highlights} onChange={(e) => set("highlights", e.target.value.split(",").map((s) => s.trim()))} className={inp} placeholder="Jardin privatif, Terrasse, Parking" data-testid="form-highlights" />
            {Array.isArray(f.highlights) && f.highlights.filter(Boolean).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="highlights-preview">
                {f.highlights.filter(Boolean).map((h, i) => (
                  <span key={i} className="inline-flex items-center px-3 py-1 rounded-full bg-[#C19A6B]/15 border border-[#C19A6B]/40 text-[#C19A6B] text-xs">{h}</span>
                ))}
              </div>
            )}
          </div>

          {/* Images */}
          <div className="space-y-3">
            <label className={lbl}>Photo de couverture</label>
            <div className="flex items-center gap-4">
              {f.cover ? <img src={f.cover} alt="" className="w-40 h-24 object-cover border border-white/10" /> : <div className="w-40 h-24 border border-dashed border-white/20 flex items-center justify-center text-zinc-600 text-xs">Aucune</div>}
              <button type="button" onClick={() => coverRef.current.click()} className="flex items-center gap-2 text-sm border border-white/10 px-4 py-2.5 text-zinc-300 hover:border-[#C19A6B]/40" data-testid="form-cover-upload"><Upload className="w-4 h-4" /> Choisir</button>
              <input ref={coverRef} type="file" accept="image/*" onChange={uploadCover} className="hidden" />
            </div>
          </div>

          <div className="space-y-3">
            <label className={lbl}>Galerie <span className="text-zinc-600 normal-case">— survolez une photo pour la déplacer, la définir en couverture ou la supprimer</span></label>
            <div className="flex flex-wrap gap-3">
              {(f.images || []).map((src, i) => (
                <div key={i} className="relative w-28 h-20 group">
                  <img src={src} alt="" className={`w-full h-full object-cover border ${f.cover === src ? "border-[#C19A6B]" : "border-white/10"}`} />
                  {f.cover === src && <span className="absolute top-1 left-1 bg-[#C19A6B] text-white text-[8px] font-bold uppercase px-1 py-0.5">Couv.</span>}
                  <button type="button" onClick={() => removeImage(i)} className="absolute -top-2 -right-2 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Supprimer"><X className="w-3 h-3" /></button>
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0} className="text-white px-1.5 py-1 disabled:opacity-30" title="Déplacer avant"><ChevronLeft className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => setAsCover(src)} className="text-[#C19A6B] px-1 py-1" title="Définir comme couverture"><Star className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => moveImage(i, 1)} disabled={i === (f.images || []).length - 1} className="text-white px-1.5 py-1 disabled:opacity-30" title="Déplacer après"><ChevronRight className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => galleryRef.current.click()} className="w-28 h-20 border border-dashed border-white/20 flex items-center justify-center text-zinc-500 hover:border-[#C19A6B]/40" data-testid="form-gallery-upload"><Upload className="w-5 h-5" /></button>
              <input ref={galleryRef} type="file" accept="image/*" multiple onChange={uploadGallery} className="hidden" />
            </div>
            {uploading && <p className="text-xs text-[#C19A6B] flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Upload en cours…</p>}
          </div>
        </div>

        {/* Colonne latérale */}
        <div className="space-y-5">
          <div><label className={lbl}>Prix (€) *</label><input required type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="form-price" /></div>
          <div>
            <label className={lbl}>Commission d'agence (taux %)</label>
            <input type="number" step="0.1" min="0" value={f.commission_rate ?? ""} onChange={(e) => set("commission_rate", e.target.value)} className={inp} data-testid="form-commission-rate" placeholder="ex. 5" />
            {Number(f.commission_rate) > 0 && Number(f.price) > 0 && (
              <p className="text-[11px] text-zinc-500 mt-1">Honoraires ≈ {Math.round(Number(f.price) * Number(f.commission_rate) / 100).toLocaleString("fr-FR")} € · apport d'acquéreur : 8% / plateforme 2%</p>
            )}
          </div>
          <div><label className={lbl}>Ville</label><input value={f.city} onChange={(e) => set("city", e.target.value)} className={inp} data-testid="form-city" /></div>
          <div><label className={lbl}>Département</label><input value={f.department} onChange={(e) => set("department", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Type</label><select value={f.type} onChange={(e) => set("type", e.target.value)} className={inp} data-testid="form-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label className={lbl}>Collection</label><select value={f.collection} onChange={(e) => set("collection", e.target.value)} className={inp} data-testid="form-collection">{COLLECTIONS.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label className={lbl}>Statut</label><select value={f.status} onChange={(e) => set("status", e.target.value)} className={inp} data-testid="form-status">{STATUSES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label className={lbl}>DPE</label><input value={f.dpe} onChange={(e) => set("dpe", e.target.value)} className={inp} placeholder="A à G" /></div>
          <div><label className={lbl}>Mot de passe spécifique (optionnel)</label><input value={f.access_password} onChange={(e) => set("access_password", e.target.value)} className={inp} placeholder="Laisser vide = mot de passe global" data-testid="form-access-password" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Latitude</label><input value={f.lat} onChange={(e) => set("lat", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Longitude</label><input value={f.lng} onChange={(e) => set("lng", e.target.value)} className={inp} /></div>
          </div>

          {/* Réseau Privé B2B — Visibilité de l'annonce */}
          <div className="border border-white/10 p-4 space-y-3" data-testid="form-visibility">
            <label className={lbl}>Visibilité de l'annonce</label>
            <div className="grid sm:grid-cols-3 gap-2">
              {[
                { id: "national", label: "National", icon: <Globe className="w-4 h-4 text-[#C19A6B]" />, desc: "Portail national + page/site du vendeur + partenaires validés." },
                { id: "network", label: "Réseau partenaires", icon: <Users className="w-4 h-4 text-[#C19A6B]" />, desc: "Vendeur + partenaires validés. Option national." },
                { id: "private", label: "Personnel", icon: <EyeOff className="w-4 h-4 text-[#C19A6B]" />, desc: "Uniquement page/site du vendeur." },
              ].map((o) => (
                <button key={o.id} type="button" onClick={() => set("visibility_mode", o.id)} data-testid={`form-vis-${o.id}`}
                  className={`text-left p-3 border transition-colors ${(f.visibility_mode || "national") === o.id ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 hover:border-white/25"}`}>
                  <div className="flex items-center gap-2 text-white text-sm font-medium">{o.icon}{o.label}</div>
                  <p className="text-[11px] text-zinc-400 mt-1 leading-snug">{o.desc}</p>
                </button>
              ))}
            </div>
            {(f.visibility_mode || "national") === "network" && (
              <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer select-none pt-1">
                <input type="checkbox" checked={!!f.network_also_national} onChange={(e) => set("network_also_national", e.target.checked)} data-testid="form-also-national" className="accent-[#C19A6B] w-4 h-4" />
                Diffuser aussi sur le portail national public
              </label>
            )}
          </div>

          <div className="border border-white/10 p-4 space-y-3" data-testid="form-video-block">
            <label className={lbl}>Vidéo du bien (prioritaire dans le hero)</label>
            <input value={f.video_url || ""} onChange={(e) => set("video_url", e.target.value)} className={inp} placeholder="Lien YouTube, Vimeo ou URL .mp4 / .webm" data-testid="form-video-url" />
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" onClick={() => videoRef.current && videoRef.current.click()} className="text-sm border border-white/10 px-4 py-2.5 text-zinc-300 hover:border-[#C19A6B]/40 transition-colors" data-testid="form-video-upload">Téléverser une vidéo</button>
              {f.video_url && <button type="button" onClick={() => set("video_url", "")} className="text-xs text-red-400 hover:underline" data-testid="form-video-remove">Retirer la vidéo</button>}
              <input ref={videoRef} type="file" accept="video/*" onChange={uploadVideo} className="hidden" />
            </div>
            {f.video_url && <p className="text-[11px] text-[#C19A6B]">Vidéo active — elle remplacera les photos dans le hero de l'annonce.</p>}
          </div>

          <label className="flex items-center gap-3 text-sm text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#C19A6B]" data-testid="form-featured" />
            Mettre en avant (à la une)
          </label>

          <label className="flex items-center gap-3 text-sm text-zinc-300 cursor-pointer border border-white/10 px-3 py-3" data-testid="form-published-label">
            <input type="checkbox" checked={!!f.published} onChange={(e) => set("published", e.target.checked)} className="w-4 h-4 accent-[#C19A6B]" data-testid="form-published" />
            <span>{f.published ? "En ligne (visible sur le site)" : "Hors ligne (brouillon) — non visible du public"}</span>
          </label>

          <button type="submit" disabled={saving} className="w-full flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm py-3.5 transition-colors disabled:opacity-60" data-testid="form-save">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…</> : <><Save className="w-4 h-4" /> {editing ? "Mettre à jour" : "Créer l'annonce"}</>}
          </button>
        </div>

        {/* Contenu local généré par IA */}
        <div className="lg:col-span-3 border border-[#C19A6B]/20 bg-[#C19A6B]/[0.03] p-6 space-y-5" data-testid="ai-content-block">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-white text-sm font-medium uppercase tracking-wider flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C19A6B]" /> Contenu local (IA)</h3>
              <p className="text-xs text-zinc-500 mt-1">Génère automatiquement le cadre de vie, les points d'intérêt et géolocalise le bien (Claude Sonnet 4.6).</p>
            </div>
            {editing ? (
              <button type="button" onClick={generateContent} disabled={genLoading} className="flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-medium uppercase tracking-wider px-5 py-3 transition-colors disabled:opacity-60" data-testid="ai-generate-btn">
                {genLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</> : <><Sparkles className="w-4 h-4" /> {ai.intro ? "Régénérer" : "Générer le contenu local"}</>}
              </button>
            ) : (
              <span className="text-xs text-zinc-500 italic">Enregistrez d'abord l'annonce pour générer le contenu.</span>
            )}
          </div>

          {(f.lat || f.lng) && <p className="text-xs text-[#C19A6B] flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Coordonnées : {f.lat}, {f.lng}</p>}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div><label className={lbl}>Introduction / cadre de vie</label><textarea rows={4} value={ai.intro || ""} onChange={(e) => setAi({ intro: e.target.value })} className={inp} data-testid="ai-intro" /></div>
            <div><label className={lbl}>Histoire locale</label><textarea rows={4} value={ai.history || ""} onChange={(e) => setAi({ history: e.target.value })} className={inp} data-testid="ai-history" /></div>
            <div><label className={lbl}>Commerces & commodités</label><textarea rows={3} value={ai.commerces || ""} onChange={(e) => setAi({ commerces: e.target.value })} className={inp} data-testid="ai-commerces" /></div>
            <div><label className={lbl}>Atouts de la commune (un par ligne)</label><textarea rows={3} value={(ai.advantages || []).join("\n")} onChange={(e) => setAi({ advantages: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} className={inp} data-testid="ai-advantages" /></div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className={lbl}>Points d'intérêt ({(ai.activities || []).length})</label>
              <button type="button" onClick={addActivity} className="flex items-center gap-1.5 text-xs text-[#C19A6B] hover:text-[#d1af84]" data-testid="ai-add-activity"><Plus className="w-3.5 h-3.5" /> Ajouter</button>
            </div>
            <div className="space-y-2">
              {(ai.activities || []).map((a, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start border border-white/5 p-2" data-testid={`ai-activity-${i}`}>
                  <input value={a.name || ""} onChange={(e) => updateActivity(i, "name", e.target.value)} placeholder="Nom" className={`${inp} sm:col-span-3`} />
                  <select value={a.category || "Culture"} onChange={(e) => updateActivity(i, "category", e.target.value)} className={`${inp} sm:col-span-2`}>{AI_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
                  <input value={a.distance || ""} onChange={(e) => updateActivity(i, "distance", e.target.value)} placeholder="Distance" className={`${inp} sm:col-span-1`} />
                  <input value={a.drive || ""} onChange={(e) => updateActivity(i, "drive", e.target.value)} placeholder="Voiture" className={`${inp} sm:col-span-1`} />
                  <input value={a.desc || ""} onChange={(e) => updateActivity(i, "desc", e.target.value)} placeholder="Description" className={`${inp} sm:col-span-3`} />
                  <div className="sm:col-span-2 flex items-center gap-1">
                    {a.image && <img src={a.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" />}
                    <button type="button" onClick={() => regenActivityImage(i)} disabled={actImgBusy === i} title="Autre image (photo publique)" className="flex-1 inline-flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-[#C19A6B] hover:text-[#d1af84] border border-[#C19A6B]/30 py-2 disabled:opacity-50" data-testid={`ai-activity-image-${i}`}>
                      {actImgBusy === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><ImageIcon className="w-3.5 h-3.5" /> Autre</>}
                    </button>
                    <button type="button" onClick={() => removeActivity(i)} className="text-red-400 hover:text-red-300 px-1"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Galerie « en images » de la ville — aperçu avant validation */}
          <div className="space-y-3" data-testid="city-gallery-block">
            <div className="flex items-center justify-between">
              <label className={lbl}>Galerie « en images » ({(ai.city_gallery || []).length})</label>
              {editing && (
                <button type="button" onClick={previewGallery} disabled={galBusy} data-testid="gallery-preview-btn" className="flex items-center gap-1.5 text-xs text-[#C19A6B] hover:text-[#d1af84] border border-[#C19A6B]/30 px-3 py-1.5 disabled:opacity-50">
                  {galBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} Chercher de nouvelles photos
                </button>
              )}
            </div>
            {(ai.city_gallery || []).length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="gallery-current">
                {(ai.city_gallery || []).map((g, i) => (
                  <div key={i} className="relative group">
                    <img src={g.image} alt={g.caption || ""} className="w-full aspect-[4/3] object-cover border border-white/10" />
                    <button type="button" onClick={() => removeGalleryImg(i)} data-testid={`gallery-remove-${i}`} className="absolute top-1 right-1 bg-red-500/80 hover:bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            {galPreview && (
              <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/[0.04] p-3 space-y-2" data-testid="gallery-preview">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-[#C19A6B] font-medium">{galPreview.length > 0 ? `Nouvelles photos trouvées (${galPreview.length}) — cliquez pour ajouter` : "Aucune nouvelle photo trouvée."}</p>
                  <div className="flex gap-3 shrink-0">
                    {galPreview.length > 0 && <button type="button" onClick={() => { galPreview.forEach(addGalleryImg); setGalPreview(null); }} data-testid="gallery-add-all" className="text-[10px] uppercase tracking-wider text-[#C19A6B] hover:text-[#d1af84]">Tout ajouter</button>}
                    <button type="button" onClick={() => setGalPreview(null)} data-testid="gallery-preview-close" className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-white">Fermer</button>
                  </div>
                </div>
                {galPreview.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {galPreview.map((c, i) => (
                      <button type="button" key={i} onClick={() => addGalleryImg(c)} data-testid={`gallery-cand-${i}`} title={c.caption || ""} className="relative group border border-white/10 hover:border-[#C19A6B] overflow-hidden">
                        <img src={c.image} alt={c.caption || ""} className="w-full aspect-[4/3] object-cover" />
                        <span className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Plus className="w-5 h-5 text-white" /></span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Home Staging IA */}
        <div className="lg:col-span-3 border border-[#C19A6B]/20 bg-[#C19A6B]/[0.03] p-6 space-y-5" data-testid="staging-block">
          <div>
            <h3 className="text-white text-sm font-medium uppercase tracking-wider flex items-center gap-2"><Wand2 className="w-4 h-4 text-[#C19A6B]" /> Home Staging IA</h3>
            <p className="text-xs text-zinc-500 mt-1">Meublez et décorez virtuellement une photo du bien (Gemini Nano Banana). L'architecture est préservée.</p>
          </div>

          {!editing ? (
            <span className="text-xs text-zinc-500 italic">Enregistrez d'abord l'annonce et ajoutez des photos pour générer un staging.</span>
          ) : (f.images || []).length === 0 ? (
            <span className="text-xs text-zinc-500 italic">Ajoutez au moins une photo (galerie) pour générer un staging.</span>
          ) : (
            <>
              <div className="space-y-2">
                <label className={lbl}>1. Choisissez la photo « avant »</label>
                <div className="flex flex-wrap gap-2" data-testid="staging-photo-picker">
                  {(f.images || []).map((img, i) => {
                    const sel = (stagingSel.image_url || f.cover || f.images[0]) === img;
                    return (
                      <button type="button" key={i} onClick={() => setStagingSel((s) => ({ ...s, image_url: img }))} data-testid={`staging-src-${i}`}
                        className={`w-24 h-16 overflow-hidden border-2 transition-colors ${sel ? "border-[#C19A6B]" : "border-transparent hover:border-white/30"}`}>
                        <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>2. Pièce</label>
                  <select value={stagingSel.room} onChange={(e) => setStagingSel((s) => ({ ...s, room: e.target.value }))} className={inp} data-testid="staging-room">
                    {STAGING_ROOMS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>3. Style {priceNum < LUXE_MIN_PRICE && <span className="text-zinc-500 normal-case">(Luxe dès 300 000 €)</span>}</label>
                  <select value={stagingSel.style} onChange={(e) => setStagingSel((s) => ({ ...s, style: e.target.value }))} className={inp} data-testid="staging-style">
                    {availableStyles.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <button type="button" onClick={generateStaging} disabled={stagingBusy} className="flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-medium uppercase tracking-wider px-5 py-3 transition-colors disabled:opacity-60" data-testid="staging-generate-btn">
                {stagingBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération (~15s)…</> : <><Wand2 className="w-4 h-4" /> Générer le staging</>}
              </button>

              {staging.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2" data-testid="staging-list">
                  {staging.map((s) => (
                    <div key={s.id} className="border border-white/10 bg-black/20" data-testid={`staging-item-${s.id}`}>
                      <div className="grid grid-cols-2">
                        <div className="relative"><img src={mediaUrl(s.before)} alt="avant" className="w-full h-28 object-cover" /><span className="absolute bottom-1 left-1 text-[9px] uppercase bg-black/60 px-1.5 py-0.5 text-white">Avant</span></div>
                        <div className="relative"><img src={mediaUrl(s.after)} alt="après" className="w-full h-28 object-cover" /><span className="absolute bottom-1 left-1 text-[9px] uppercase bg-[#C19A6B] px-1.5 py-0.5 text-white">Après</span></div>
                      </div>
                      <div className="flex items-center justify-between px-3 py-2">
                        <span className="text-xs text-zinc-300">{s.room} · {s.style}</span>
                        <button type="button" onClick={() => deleteStaging(s.id)} className="text-red-400 hover:text-red-300" data-testid={`staging-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* SEO & FAQ */}
        <div className="lg:col-span-3 border border-white/10 bg-white/[0.02] p-6 space-y-5" data-testid="seo-block">
          <h3 className="text-white text-sm font-medium uppercase tracking-wider">SEO & données structurées</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div><label className={lbl}>Titre SEO (meta title)</label><input value={seo.meta_title || ""} onChange={(e) => setSeo({ meta_title: e.target.value })} className={inp} data-testid="seo-meta-title" /></div>
            <div><label className={lbl}>Meta description</label><input value={seo.meta_description || ""} onChange={(e) => setSeo({ meta_description: e.target.value })} className={inp} data-testid="seo-meta-desc" /></div>
            <div className="lg:col-span-2"><label className={lbl}>Caractéristiques (une par ligne)</label><textarea rows={3} value={(f.features || []).join("\n")} onChange={(e) => set("features", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} className={inp} data-testid="form-features" /></div>
          </div>
          {(seo.faq || []).length > 0 && (
            <div className="space-y-2" data-testid="seo-faq">
              <label className={lbl}>FAQ générée ({seo.faq.length})</label>
              {seo.faq.map((q, i) => (
                <div key={i} className="border border-white/10 p-3 text-xs">
                  <p className="text-zinc-200 font-medium">{q.q}</p>
                  <p className="text-zinc-400 mt-1">{q.a}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </form>
    </AdminShell>
  );
}
